"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

interface ChainLevel {
  typ: string;
  namn: string | null;
}

interface ChainResponse {
  chain: ChainLevel[];
  deepestExit: "larare" | "admin" | null;
}

// Visas högst upp på alla sidor (utom publika) när man är inloggad som någon
// annan. Visar HELA kedjan med kontotyp + namn per nivå, t.ex.
//   "Superadmin inloggad som: Lärare Pelle Nilson → Elev Saga Persson"
//   "Skoladmin Järvsögymnasiet inloggad som: Elev Olov Isaksson"
// Plus EN "Logga ut"-knapp som tar bort den djupaste nivån (steg för steg).
// Kedjan + exit-mål kommer från /api/impersonation/chain (login-as-stacken
// + lärar-impersonation upplöst server-sidigt).
function label(level: ChainLevel): string {
  return level.namn ? `${level.typ} ${level.namn}` : level.typ;
}

export default function SessionImpersonationBanner({ utanKedja = false }: { utanKedja?: boolean }) {
  const pathname = usePathname();
  const [data, setData] = useState<ChainResponse | null>(null);
  const [exiting, setExiting] = useState(false);

  const isPublicPath =
    pathname === "/login" ||
    pathname?.startsWith("/glomt-losenord") ||
    pathname?.startsWith("/aterstall-losenord") ||
    pathname === "/m" ||
    pathname?.startsWith("/m/");

  useEffect(() => {
    // utanKedja: a real student's own login (decided by the layout per page load) — no read at all.
    // Until 2026-10-31 every student fetched the (always empty) chain on every page change.
    if (isPublicPath || utanKedja) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/impersonation/chain", {
          cache: "no-store",
        });
        if (cancelled || !res.ok) return;
        const json = (await res.json()) as ChainResponse;
        setData(json.chain.length > 0 ? json : null);
      } catch {
        if (!cancelled) setData(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname, isPublicPath, utanKedja]);

  // Skjut sidomeny-toggle-knappen ner medan bannern är synlig.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const visible = !isPublicPath && data !== null;
    document.body.setAttribute(
      "data-impersonation-banner",
      visible ? "1" : "0",
    );
    return () => {
      if (typeof document !== "undefined") {
        document.body.setAttribute("data-impersonation-banner", "0");
      }
    };
  }, [isPublicPath, data]);

  const handleExit = async () => {
    if (!data?.deepestExit) return;
    setExiting(true);
    try {
      const endpoint =
        data.deepestExit === "admin"
          ? "/api/admin/restore"
          : "/api/impersonate/exit";
      const res = await fetch(endpoint, { method: "POST" });
      if (!res.ok) {
        setExiting(false);
        return;
      }
      const body = (await res.json()) as { redirect?: string };
      window.location.href =
        body.redirect || (data.deepestExit === "admin" ? "/admin" : "/");
    } catch {
      setExiting(false);
    }
  };

  if (isPublicPath || !data || data.chain.length === 0) return null;

  const root = data.chain[0];
  const rest = data.chain.slice(1);
  const deepest = data.chain[data.chain.length - 1];

  // "Root inloggad som: A → B → C". Om bara root finns (ingen kedja) har
  // endpointen redan returnerat tom chain → vi har returnerat null ovan.
  const meddelande =
    rest.length > 0
      ? `${label(root)} inloggad som: ${rest.map(label).join(" → ")}`
      : label(root);

  return (
    <div
      className="sticky top-0 z-40 w-full border-b"
      style={{
        background: "var(--color-warning-soft)",
        borderColor: "var(--color-warning)",
      }}
    >
      <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-between gap-4 flex-wrap">
        <span className="text-sm text-[var(--color-text)]">{meddelande}</span>
        {data.deepestExit && (
          <button
            type="button"
            onClick={handleExit}
            disabled={exiting}
            title="Logga ut den djupaste inloggningen — gå tillbaka ett steg"
            className="text-xs font-medium px-3 py-1.5 rounded-md border border-[var(--color-warning)] text-[var(--color-warning)] hover:bg-[var(--color-warning)]/15 transition-colors disabled:opacity-40 shrink-0"
          >
            {exiting ? "Loggar ut…" : `Logga ut: ${label(deepest)}`}
          </button>
        )}
      </div>
    </div>
  );
}
